import { z } from 'zod';

import { CORPORATE, normalizePromoCode } from '../constants/corporate';
import { IdSchema, KopecksSchema } from './common';

/**
 * Корпоративные сезоны (BLUEPRINT Stage 14, задача 4): заявка с лендинга `/corp` → ops заводит
 * компанию и выставляет счёт вне системы (`Order CORPORATE MANUAL`) → сотрудники входят по
 * промокоду и попадают в приоритетный пул компании.
 */

export const PromoCodeSchema = z
  .string()
  .trim()
  .min(4, 'Промокод от 4 символов')
  .max(24)
  .transform(normalizePromoCode)
  .pipe(z.string().regex(/^[A-Z0-9]{4,24}$/, 'Промокод: латиница и цифры'));

// ───────────────────────────── заявка с лендинга ─────────────────────────────

export const CorpLeadInputSchema = z.object({
  companyName: z.string().trim().min(2, 'Название компании от 2 символов').max(120),
  contactName: z.string().trim().min(2, 'Как к вам обращаться').max(80),
  /** Телефон или @username — любой канал связи */
  contact: z.string().trim().min(3, 'Оставь контакт').max(200),
  cityId: IdSchema.nullable().default(null),
  /** Сколько сотрудников хотят играть */
  people: z.number().int().min(2).max(CORPORATE.maxSeats),
  comment: z.string().trim().max(1000).nullable().default(null),
});
export type CorpLeadInput = z.infer<typeof CorpLeadInputSchema>;

export const CorpLeadResultSchema = z.object({ ok: z.literal(true) });

/** Текстовые блоки лендинга приходят с сервера — цены и размер отряда не хардкодятся в web. */
export const CorpOfferSchema = z.object({
  /** Цена сезона на человека без скидки, копейки */
  seasonPriceKopecks: KopecksSchema,
  gatherings: z.number().int().positive(),
  /** Города, где можно запуститься */
  cities: z.array(z.object({ id: IdSchema, name: z.string() })),
  /** Поддержка для вопросов, без @ */
  supportUsername: z.string().nullable(),
});
export type CorpOffer = z.infer<typeof CorpOfferSchema>;

// ───────────────────────────── промокод сотрудника ─────────────────────────────

export const CompanyPromoApplyInputSchema = z.object({ promoCode: PromoCodeSchema });
export type CompanyPromoApplyInput = z.infer<typeof CompanyPromoApplyInputSchema>;

/** Компания глазами сотрудника: без счетов и контактов. */
export const CompanyPublicSchema = z.object({
  id: IdSchema,
  name: z.string(),
  discountPct: z.number().int().min(0).max(100),
  /** Отряды собираются только из сотрудников */
  employeesOnly: z.boolean(),
  cityId: IdSchema.nullable(),
});
export type CompanyPublic = z.infer<typeof CompanyPublicSchema>;

// ───────────────────────────── админка ─────────────────────────────

export const AdminCompanyFieldsSchema = z.object({
  name: z.string().trim().min(2, 'Название от 2 символов').max(120),
  cityId: IdSchema.nullable(),
  inn: z
    .string()
    .trim()
    .regex(/^\d{10}(\d{2})?$/, 'ИНН — 10 или 12 цифр')
    .nullable(),
  contact: z.string().trim().min(3, 'Контакт обязателен').max(200),
  seatsPurchased: z.number().int().min(0).max(CORPORATE.maxSeats),
  discountPct: z.number().int().min(0).max(CORPORATE.maxDiscountPct),
  mixedSquads: z.boolean(),
  isActive: z.boolean(),
  notes: z.string().trim().max(2000).nullable(),
});

export const AdminCompanyCreateInputSchema = AdminCompanyFieldsSchema.extend({
  cityId: AdminCompanyFieldsSchema.shape.cityId.default(null),
  inn: AdminCompanyFieldsSchema.shape.inn.default(null),
  /** Пусто — сервер сгенерирует код */
  promoCode: PromoCodeSchema.optional(),
  seatsPurchased: AdminCompanyFieldsSchema.shape.seatsPurchased.default(0),
  discountPct: AdminCompanyFieldsSchema.shape.discountPct.default(0),
  mixedSquads: z.boolean().default(true),
  isActive: z.boolean().default(true),
  notes: AdminCompanyFieldsSchema.shape.notes.default(null),
});
export type AdminCompanyCreateInput = z.infer<typeof AdminCompanyCreateInputSchema>;

export const AdminCompanyUpdateInputSchema = AdminCompanyFieldsSchema.partial().extend({
  companyId: IdSchema,
  promoCode: PromoCodeSchema.optional(),
});
export type AdminCompanyUpdateInput = z.infer<typeof AdminCompanyUpdateInputSchema>;

export const AdminCompanyIdInputSchema = z.object({ companyId: IdSchema });

export const AdminCompaniesListInputSchema = z
  .object({ cityId: IdSchema.optional(), includeInactive: z.boolean().default(false) })
  .default({ includeInactive: false });

/** Счёт компании за места: выставляется вне системы, в базе — `Order CORPORATE MANUAL`. */
export const AdminCompanyInvoiceInputSchema = z.object({
  companyId: IdSchema,
  seats: z.number().int().min(1).max(CORPORATE.maxSeats),
  amountKopecks: KopecksSchema,
  /** Счёт уже оплачен по банку — отметить сразу PAID */
  paid: z.boolean().default(false),
});
export type AdminCompanyInvoiceInput = z.infer<typeof AdminCompanyInvoiceInputSchema>;

export const AdminCompanyRowSchema = z.object({
  id: IdSchema,
  name: z.string(),
  cityId: IdSchema.nullable(),
  cityName: z.string().nullable(),
  inn: z.string().nullable(),
  contact: z.string(),
  promoCode: z.string(),
  seatsPurchased: z.number().int().nonnegative(),
  discountPct: z.number().int().nonnegative(),
  mixedSquads: z.boolean(),
  isActive: z.boolean(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  /** Сотрудников вошло по промокоду */
  employees: z.number().int().nonnegative(),
  /** Сотрудников стоит в пуле */
  inPool: z.number().int().nonnegative(),
  /** Сотрудников попало в отряды */
  placed: z.number().int().nonnegative(),
  /** Оплачено счетами компании, копейки */
  paidKopecks: KopecksSchema,
});
export type AdminCompanyRow = z.infer<typeof AdminCompanyRowSchema>;

/** Заявка с лендинга живёт в `Event`, отдельной таблицы под неё не заводим. */
export const AdminCorpLeadRowSchema = z.object({
  eventId: IdSchema,
  createdAt: z.string(),
  companyName: z.string(),
  contactName: z.string(),
  contact: z.string(),
  cityName: z.string().nullable(),
  people: z.number().int().nonnegative(),
  comment: z.string().nullable(),
  /** Компания с таким названием уже заведена */
  companyId: IdSchema.nullable(),
});
export type AdminCorpLeadRow = z.infer<typeof AdminCorpLeadRowSchema>;

